import { useNavigate } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { useCallback } from 'react';
import { Button, Text } from '@/shared/ui';
import { TARGET_ROUTES } from '../model/handle-navigate-handoff';
import { useAgentPanelStore } from '../model/use-agent-panel-store';
import css from './navigate-handoff-card.module.scss';

// Confirm card surfaced when the planner returns `phase: 'navigate'`.
// Two actions:
//   - "Take me there" → stash the original utterance in
//     `pendingHandoffReplay`, navigate to the target route, clear the
//     pending nav. The replay-on-mount effect in `useAgentCommand` then
//     fires `startSession` on the new page so the LLM re-plans against
//     the right tool catalog.
//   - "Cancel" → clear `pendingNavigation`. URL stays put, panel returns
//     to idle. No side effects.
//
// Renders nothing when `pendingNavigation` is unset, so the panel body
// can safely render `<NavigateHandoffCard />` unconditionally.
export const NavigateHandoffCard = () => {
  const navigate = useNavigate();
  const pending = useAgentPanelStore((s) => s.pendingNavigation);
  const setPendingNavigation = useAgentPanelStore(
    (s) => s.setPendingNavigation
  );
  const setPendingHandoffReplay = useAgentPanelStore(
    (s) => s.setPendingHandoffReplay
  );

  const onConfirm = useCallback(() => {
    if (!pending) return;
    setPendingHandoffReplay({
      target: pending.target,
      utterance: pending.replayUtterance,
    });
    setPendingNavigation(null);
    void navigate({ to: TARGET_ROUTES[pending.target] });
  }, [pending, navigate, setPendingHandoffReplay, setPendingNavigation]);

  const onCancel = useCallback(() => {
    setPendingNavigation(null);
  }, [setPendingNavigation]);

  if (!pending) return null;

  return (
    <div className={css.root} data-testid="navigate-handoff-card" role="dialog">
      <Text size="sm" weight="medium">
        {pending.reason}
      </Text>
      <div className={css.actions}>
        <Button onClick={onConfirm} size="sm" variant="primary">
          <span className={css.confirmLabel}>
            Take me there
            <ArrowRight aria-hidden="true" size={14} />
          </span>
        </Button>
        <Button onClick={onCancel} size="sm" variant="ghost">
          Cancel
        </Button>
      </div>
    </div>
  );
};
